---
title: "Setting SL/TP and Trailing Stops on {{BRAND}}"
slug: setting-tp-sl-trailing-stops
shortTitle: "SL, TP & Trailing Stops"
description: "How to set a stop loss and take profit on {{BRAND}} as a price or a USDT amount, edit or remove them on open trades, and trail your stop by hand."
level: intermediate
category: trading
course: platform-guide
order: 4
topics: [risk-management]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/setting-tp-sl-trailing-stops.svg
faqs:
  - q: "Does {{BRAND}} offer an automatic trailing stop?"
    a: "Not at the moment. You can trail a stop by hand instead: move your SL with Edit TP / SL on the open position, or drag the SL line on the desktop chart."
  - q: "Will my stop loss always fill at exactly my price?"
    a: "No. Once the price reaches your level, a stop loss closes the position at market, so in a fast market or after a price gap it can fill at a worse price than the one you set."
  - q: "Which price triggers my TP and SL?"
    a: "The price your position would close at. A buy closes at the Bid, so its TP and SL trigger on the Bid; a sell closes at the Ask, so its levels trigger on the Ask."
  - q: "How do I remove a take profit or stop loss?"
    a: "In the Open Positions tab, open Edit TP / SL on the position, clear the field you want to remove and save. An empty field means that level is removed."
  - q: "Should I set my stop as a price or as a loss amount?"
    a: "Both end up as a price level. A price suits a stop placed at a chart level; a Loss (USDT) amount suits a stop sized to a fixed risk. Either way, check that the resulting price makes sense on the chart."
---

A **stop loss (SL)** closes a position automatically when the price moves against you by as much as you decided to accept. A **take profit (TP)** closes it automatically at your target. Together they turn a plan into orders that keep working while you are away from the screen.

On {{BRAND}} you can set both when you open a trade, change them later, drag them on the desktop chart, or remove them. There is no automatic trailing stop, but you can trail a stop by hand, and this guide shows how. For the ideas behind stops and targets, read [Stop Loss and Take Profit](/academy/article/stop-loss-take-profit) first.

<Warning title="A stop loss limits risk; it does not remove it">

Once triggered, a stop loss closes your position at market. In fast markets, or when the price gaps over a weekend or on news, the fill can be well beyond your level. Leverage magnifies every loss, and most traders lose money trading CFDs.

</Warning>

<CTA />

<KeyTakeaways>

- **Set TP / SL as a price or an amount.** The TP / SL switch takes a TP Price and SL Price, or a Profit (USDT) and Loss (USDT), and shows the % distance from entry.
- **Levels trigger on the closing price.** Buys close at the Bid and sells at the Ask, so the spread matters for tight stops.
- **Edit, drag or clear at any time.** Use Edit TP / SL in Open Positions, drag the lines on the desktop chart, or empty a field to remove it.
- **Trailing is manual.** There is no automatic trailing stop; you move your SL yourself, by a rule you set in advance.

</KeyTakeaways>

## Setting TP / SL when you open a trade

<Steps>
<Step title="Build the order">

Choose **Buy** or **Sell**, the order tab, your size and your leverage as usual.

</Step>
<Step title="Turn on the TP / SL switch">

The take profit and stop loss fields appear in the order panel.

</Step>
<Step title="Enter a price or an amount">

Type a **TP Price** and **SL Price**, or a **Profit (USDT)** and **Loss (USDT)**. The panel shows how far each level is from your entry, as a percentage.

</Step>
<Step title="Check the direction and confirm">

For a buy, TP sits above the entry and SL below it. For a sell it is the other way round. Then check the summary and confirm.

</Step>
</Steps>

<Screenshot name="order-panel-tp-sl" alt="The order panel with the TP / SL switch on, showing TP Price, SL Price and their percentage distance from entry" caption="The TP / SL switch: set levels as prices or as USDT amounts." />

{/* TODO: verify with platform — how the panel switches between price and USDT-amount entry, and whether Profit / Loss (USDT) allow for the spread and fee. */}

<Example title="Setting levels by amount and by price (illustrative prices)">

**A buy, by amount.** You buy 0.02 BTC at 60,000 and enter a **Loss (USDT)** of 50 and a **Profit (USDT)** of 100.

- SL: 60,000 − (50 ÷ 0.02) = 60,000 − 2,500 = **57,500**, about 4.2% below entry.
- TP: 60,000 + (100 ÷ 0.02) = 60,000 + 5,000 = **65,000**, about 8.3% above entry.

**A sell, by price.** You sell 0.05 lots of GOLD/USDT (5 ounces) at 2,400, with an **SL Price** of 2,430 and a **TP Price** of 2,340.

- If the SL is hit: (2,400 − 2,430) × 5 = **−150 USDT** before costs.
- If the TP is hit: (2,400 − 2,340) × 5 = **+300 USDT** before costs.

</Example>

## Which price triggers your levels

TP and SL trigger on the price your position would close at. A buy closes at the **Bid**, so its levels watch the Bid. A sell closes at the **Ask**, so its levels watch the Ask.

This matters most for sells and for tight stops. Say you are short gold with an SL at 2,430, and the quote moves to Bid 2,428 and Ask 2,431. The Ask has crossed 2,430, so your stop triggers, even though the Bid is still below it. Showing the bid and ask lines in the chart settings makes this easier to see.

## Choosing sensible levels

The order panel will accept almost any level, so the judgment is yours. A few habits help:

- **Put the stop where your idea is wrong,** for example just beyond a recent swing low for a buy, rather than at a round USDT amount picked for comfort.
- **Then size the trade to fit the stop.** If the right stop is far away, trade smaller rather than moving the stop closer.
- **Leave room for the spread and normal swings.** A stop only slightly beyond the current Bid or Ask can be hit by ordinary noise.
- **Check the reward against the risk.** If the TP is closer than the SL, each win has to cover a bigger loss.

<Example title="Sizing a trade to fit the stop (illustrative prices)">

You plan to buy BTC/USDT at 60,000. The chart suggests a stop at 57,500, which is 2,500 away, and you are willing to lose 50 USDT. The size is 50 ÷ 2,500 = **0.02 BTC**, a position worth 0.02 × 60,000 = 1,200 USDT. If the stop needed to be 5,000 away instead, the same 50 USDT of risk allows 50 ÷ 5,000 = **0.01 BTC**.

</Example>

[Position Sizing](/academy/article/position-sizing) turns this into a repeatable method.

## Changing or removing TP / SL on an open position

<Steps>
<Step title="Find the position">

Open the **Open Positions** tab below the chart. The **TP / SL** column shows the current levels.

</Step>
<Step title="Open Edit TP / SL">

In the **Actions** column, choose **Edit TP / SL**. A dialog opens with the price fields.

</Step>
<Step title="Change or clear a level">

Type a new price, or empty a field to remove that level entirely.

</Step>
<Step title="Save and check">

Save the change and check the **TP / SL** column shows what you expect.

</Step>
</Steps>

<Screenshot name="edit-tp-sl-dialog" alt="The Edit TP / SL dialog for an open position with take profit and stop loss price fields" caption="Edit TP / SL: change a price, or clear a field to remove it." />

On desktop you can also drag the TP or SL line on the chart to a new price. The platform asks you to confirm before the change is applied, so a slip of the mouse does not move your stop by accident.

<Screenshot name="chart-drag-sl-line" alt="A stop loss line being dragged on the desktop chart with a confirmation prompt" caption="Drag a TP or SL line on the desktop chart, then confirm the new level." />

<ProTip>

Move a stop loss only toward the price, to reduce risk or lock in profit, never away from it. Widening a stop because the trade is going against you turns a planned loss into an unplanned one.

</ProTip>

## Trailing stops: doing it by hand

A **trailing stop** follows the price as it moves in your favor, keeping a set distance behind it, and never moves back. Some platforms automate this. {{BRAND}} does not currently offer an automatic trailing stop, so you trail by moving your SL yourself.

{/* TODO: verify with platform — confirm automatic trailing stops are still not offered. */}

<CompareTable caption="Three ways to trail a stop by hand">

| Method | How you move the SL | Strength | Weakness |
| --- | --- | --- | --- |
| Fixed distance | Keep it a set distance behind the best price | Simple and mechanical | Ignores chart structure |
| Structure | Move it below each new higher low (for a buy) | Follows how the market moves | Wider, and needs judgment |
| Break-even step | Move it to your entry once the trade is in profit by a set amount | Removes the starting risk | Easily stopped out by normal swings |

</CompareTable>

Break-even is not quite zero: the spread, fee and any swap mean a stop at your entry price still closes slightly negative. The chart settings can show a break-even line for your position, which helps here.

<Steps>
<Step title="Decide the rule before you enter">

Write down the method and the distance, for example "trail 2,500 behind the highest price".

</Step>
<Step title="Set price alerts at your trigger levels">

Use **Account → Alerts** or the bell on the market row so you are told when it is time to move the stop.

</Step>
<Step title="Move the SL when the alert fires">

Use **Edit TP / SL**, or drag the SL line on the desktop chart, and confirm.

</Step>
</Steps>

<Example title="Trailing a Bitcoin stop by hand (illustrative prices)">

You buy 0.02 BTC at 60,000 with an SL at 57,500, a trail distance of 2,500 and 50 USDT at risk.

- Price reaches 63,000: move the SL to 63,000 − 2,500 = 60,500. Worst case is now (60,500 − 60,000) × 0.02 = **+10 USDT** before costs.
- Price reaches 65,000: move the SL to 62,500. Worst case is now (62,500 − 60,000) × 0.02 = **+50 USDT** before costs.
- Price falls back to 62,500: the stop closes the trade at about **+50 USDT**, less costs and any slippage.

</Example>

To bank part of a winner instead, desktop Instant Trade can close 25% or 50% of a position. [Scaling In and Out, Trailing Stops and Partial Take-Profit](/academy/article/scaling-trailing-stops-partial-take-profit) covers those techniques in depth.

## Conclusion

On {{BRAND}}, the **TP / SL** switch sets your exits as prices or USDT amounts, **Edit TP / SL** and the desktop chart lines let you change them, and an empty field removes them. Levels trigger on the closing price, and a stop can slip in fast markets. Trailing is a manual job, so decide the rule before you enter and use alerts to tell you when to act.
